import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Dropdown from '@orn/shared-web/components/Dropdown/Dropdown';
import { InlineInternalOnlyBadge } from '@orn/shared-web/components/InternalOnlyBadge';
import { CodeIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';

/**
 * Avatar-dropdown submenu surfacing developer-only debug routes
 * (`Sentry test`, `Environment`) gated on `FeatureGate.DebugTools`.
 * Renders nothing when the gate is closed (fail-closed). Designed to
 * be dropped into any `Dropdown.Menu` — currently used by
 * `NavAccountCard` (full sidebar / mobile drawer) and
 * `IconRailAccountButton` (md icon rail) so the entry surfaces at
 * every viewport that exposes an account dropdown.
 *
 * The "Internal only" badge is rendered as an inline pip after the
 * `Debug` label (and after each sub-item label) rather than overlaid
 * on the icon — keeps a single annotation pattern across the trigger
 * and its children.
 */
const DebugAccountSubmenu = (): React.ReactElement | null => {
  const navigate = useNavigate();
  const showDebugTools = useFeatureGate(FeatureGate.DebugTools);

  if (!showDebugTools) {
    return null;
  }

  return (
    <Dropdown.Submenu>
      <Dropdown.Item icon={CodeIcon}>
        Debug
        <InlineInternalOnlyBadge />
      </Dropdown.Item>
      <Dropdown.Popover placement="right top">
        <Dropdown.Menu>
          <Dropdown.Item onAction={() => navigate(PATHS.DEBUG_ENVIRONMENT)}>
            Environment
            <InlineInternalOnlyBadge />
          </Dropdown.Item>
          <Dropdown.Item onAction={() => navigate(PATHS.DEBUG_SENTRY_TEST)}>
            Sentry test
            <InlineInternalOnlyBadge />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Submenu>
  );
};

export default DebugAccountSubmenu;
